export interface RebateTableColumn {
    key: string;
    title: string;
    isAmount?: boolean;
}

export interface RebateCategory {
    key: string;
    title: string;
    columns: RebateTableColumn[];
    actualAmountKeys: string[];
    allowableAmountKeys?: string[];
}

export const REBATE_CATEGORIES: RebateCategory[] = [
    {
        key: 'RB_CAT_LIFE_INS_PREM',
        title: 'Life Insurance Premium',
        columns: [
            { key: 'RB_POLICY_NUMBER', title: 'Life Insurance Policy Number' },
            { key: 'RB_INS_COMPANY', title: 'Insurance Company' },
            { key: 'RB_POLICY_VAL', title: 'Policy Value', isAmount: true },
            { key: 'RB_PREM_PAID', title: 'Premium Paid', isAmount: true },
            { key: 'RB_ALLOWABLE_LIP', title: 'Allowable Amount', isAmount: true },
        ],
        actualAmountKeys: ['RB_PREM_PAID'],
        allowableAmountKeys: ['RB_ALLOWABLE_LIP'],
    },
    {
        key: 'RB_CAT_DPS',
        title: 'Deposit Pension Scheme (DPS)',
        columns: [
            { key: 'RB_BANK_FI', title: 'Bank/FI' },
            { key: 'RB_ACC_NO', title: 'Account No' },
            { key: 'RB_DEPOSIT_AMT', title: 'Deposit Amount', isAmount: true },
            { key: 'RB_ALLOWABLE_DPS', title: 'Allowable Amount', isAmount: true },
        ],
        actualAmountKeys: ['RB_DEPOSIT_AMT'],
        allowableAmountKeys: ['RB_ALLOWABLE_DPS'],
    },
    {
        key: 'RB_CAT_APPR_SAVINGS_CERT',
        title: 'Approved Sanchayapatra & Other Govt. Securities',
        columns: [
            { key: 'RB_NM', title: 'Name of the Instrument' },
            { key: 'RB_REG_NO', title: 'Registration/Instrument No.' },
            { key: 'RB_DT', title: 'Issue Date' },
            { key: 'RB_INVESTMENT_AMT', title: 'Investment Amount', isAmount: true },
            { key: 'RB_ALLOWABLE_APPR_SAVINGS_CERT', title: 'Allowable Amount', isAmount: true },
        ],
        actualAmountKeys: ['RB_INVESTMENT_AMT'],
        allowableAmountKeys: ['RB_ALLOWABLE_APPR_SAVINGS_CERT'],
    },
    {
        key: 'RB_CAT_UNIT_CERT',
        title: 'Unit Certificate/Mutual Fund/ETF/Joint Investment Scheme',
        columns: [
            { key: 'RB_NM', title: 'Name' },
            { key: 'RB_REG_NO', title: 'Account No.' },
            { key: 'RB_DT', title: 'Issue Date' },
            { key: 'RB_INVESTMENT_AMT', title: 'Investment Amount', isAmount: true },
            { key: 'RB_ALLOWABLE_UNIT_CERT', title: 'Allowable Amount', isAmount: true },
        ],
        actualAmountKeys: ['RB_INVESTMENT_AMT'],
        allowableAmountKeys: ['RB_ALLOWABLE_UNIT_CERT'],
    },
    {
        key: 'RB_CAT_APPR_STOCKS_OR_SHARES',
        title: 'Listed Stocks or Shares',
        columns: [
            { key: 'RB_BO_ACC_NO', title: 'BO Account No.' },
            { key: 'RB_BR_HOUSE_NM', title: 'Brokerage House Name' },
            { key: 'RB_INVESTMENT_AMT', title: 'Investment During the Year', isAmount: true },
        ],
        actualAmountKeys: ['RB_INVESTMENT_AMT'],
    },
    {
        key: 'RB_CAT_GPF',
        title: 'General Provident Fund (GPF)',
        columns: [
            { key: 'RB_ACC_NO', title: 'Account No.' },
            { key: 'RB_INVESTMENT_AMT', title: 'Contribution', isAmount: true },
        ],
        actualAmountKeys: ['RB_INVESTMENT_AMT'],
    },
    {
        key: 'RB_CAT_RPF',
        title: 'Recognized Provident Fund (RPF)',
        columns: [
            { key: 'RB_EMPLOYER_NM', title: 'Employer Name' },
            { key: 'RB_SELF_CONTRIBUTION', title: 'Self Contribution', isAmount: true },
            { key: 'RB_EMPLOYER_CONTRIBUTION', title: 'Employer Contribution', isAmount: true },
        ],
        actualAmountKeys: ['RB_SELF_CONTRIBUTION', 'RB_EMPLOYER_CONTRIBUTION'],
    },
    {
        key: 'RB_CAT_APPR_SUPER_ANNUATION_FUND',
        title: 'Approved Superannuation Fund',
        columns: [
            { key: 'RB_NM', title: 'Name of the Superannuation Fund' },
            { key: 'RB_DT', title: 'Date' },
            { key: 'RB_INVESTMENT_AMT', title: 'Contribution Amount', isAmount: true },
        ],
        actualAmountKeys: ['RB_INVESTMENT_AMT'],
    },
    {
        key: 'RB_CAT_BENEVOLENT_FUND_GRP_INS_PREM',
        title: 'Approved Benevolent Fund & Group Insurance Premium',
        columns: [
            { key: 'RB_BENEVOLENT_AMT', title: 'Contribution of Benevolent Fund', isAmount: true },
            { key: 'RB_INVESTMENT_AMT', title: 'Group Insurance Premium', isAmount: true },
        ],
        actualAmountKeys: ['RB_BENEVOLENT_AMT', 'RB_INVESTMENT_AMT'],
    },
    {
        key: 'RB_CAT_ZAKAT_FUND',
        title: 'Zakat Fund (Under Zakat Fund Management Act 2023)',
        columns: [
            { key: 'RB_NM', title: 'Name of the Zakat Fund and Institute' },
            { key: 'RB_DT', title: 'Date' },
            { key: 'RB_INVESTMENT_AMT', title: 'Contribution Amount', isAmount: true },
        ],
        actualAmountKeys: ['RB_INVESTMENT_AMT'],
    },
    {
        key: 'RB_CAT_PENSION_SCHEME',
        title: 'Universal Pension Scheme',
        columns: [
            { key: 'RB_NM', title: 'Name of the Scheme' },
            { key: 'RB_ID_NUMBER', title: 'Pension ID Number' },
            { key: 'RB_INVESTMENT_AMT', title: 'Investment Amount', isAmount: true },
        ],
        actualAmountKeys: ['RB_INVESTMENT_AMT'],
    },
    {
        key: 'RB_CAT_OTHERS_INVESTMENT_U_6_B',
        title: 'Investment Under 6th Schedule Part 3',
        columns: [
            { key: 'RB_PARA_NUM', title: 'Paragraph Number' },
            { key: 'RB_DESCRIPTION', title: 'Description' },
            { key: 'RB_INVESTMENT_AMT', title: 'Amount', isAmount: true },
        ],
        actualAmountKeys: ['RB_INVESTMENT_AMT'],
    },
    {
        key: 'RB_CAT_OTHERS_SRO',
        title: 'Investment Under SRO',
        columns: [
            { key: 'RB_SRO_NO', title: 'SRO No.' },
            { key: 'RB_YR', title: 'Year' },
            { key: 'RB_DESCRIPTION', title: 'Description' },
            { key: 'RB_INVESTMENT_AMT', title: 'Amount', isAmount: true },
        ],
        actualAmountKeys: ['RB_INVESTMENT_AMT'],
    },
];
